import { FeedbackError } from '@/components/Feedback';
import Lightbox from '@/components/Lightbox';
import { CommentMedia } from '@/hooks/Feedback/interfaces';
import { useDeleteCommentMedia } from '@/hooks/Feedback/useDeleteCommentMedia';
import { useGhostMedias } from '@/hooks/Feedback/useGhostMedias';
import { useRetryCommentMedia } from '@/hooks/Feedback/useRetryCommentMedia';
import { formatFileSize } from '@/utils/formatFileSize';
import {
  Box,
  Center,
  Circle,
  CircularProgress,
  Flex,
  Icon,
  Image,
  Skeleton,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiUploadCloud, FiX } from 'react-icons/fi';

import { CommentAudioPlayer } from './CommentAudioPlayer';
import { getFileChipConfig, getMediaKind } from './mediaType';
import { openViaProxy } from './openViaProxy';

type Props = {
  medias: CommentMedia[];
  commentId: string;
  topicId: string;
};

export const CommentMediaList: React.FC<Props> = ({
  medias,
  commentId,
  topicId,
}) => {
  const { t, i18n } = useTranslation();
  const { mutateAsync: cancelMedia } = useDeleteCommentMedia();
  const { mutateAsync: retryMedia } = useRetryCommentMedia(topicId);
  // Map unificado persistido em localStorage — sobrevive à navegação.
  const [cancelledMedias, setCancelledMedias] = useGhostMedias(commentId);

  // Ghosts = cancelados que o servidor já não retorna mais
  const visibleGhosts = React.useMemo(
    () =>
      [...cancelledMedias.values()].filter(
        (g) => !medias.find((m) => m.id === g.id),
      ),
    [cancelledMedias, medias],
  );

  const handleCancel = React.useCallback(
    async (media: CommentMedia) => {
      if (media.id.includes('temp-media')) return;
      setCancelledMedias((prev) => new Map([...prev, [media.id, media]]));
      try {
        await cancelMedia({ mediaId: media.id });
      } catch {
        setCancelledMedias((prev) => {
          const next = new Map(prev);
          next.delete(media.id);
          return next;
        });
      }
    },
    [cancelMedia, setCancelledMedias],
  );

  const dismissGhost = React.useCallback(
    (mediaId: string) => {
      setCancelledMedias((prev) => {
        const next = new Map(prev);
        next.delete(mediaId);
        return next;
      });
    },
    [setCancelledMedias],
  );

  const handleRetry = React.useCallback(
    async (ghost: CommentMedia) => {
      try {
        await retryMedia({
          commentId,
          content: ghost.content,
          fileName: ghost.fileName,
          fileSize: ghost.fileSize,
        });
        dismissGhost(ghost.id);
      } catch (err) {
        FeedbackError({
          mainText: t('error.warning'),
          subText:
            err instanceof Error ? err.message : t('error.unexpectedError'),
        });
        // mantém ghost com botão retry disponível
      }
    },
    [commentId, dismissGhost, retryMedia, t],
  );

  const imageCount = React.useMemo(
    () =>
      medias.filter(
        (m) =>
          getMediaKind(m.fileName || m.fileUrl || m.content, m.mediaType) ===
          'image',
      ).length,
    [medias],
  );

  const THUMB_SIZE = imageCount === 1 ? '276px' : '137px';

  // Pré-calcula lista de URLs de imagens processadas para o Lightbox —
  // os índices devem corresponder apenas a itens de imagem processados.
  const processedImages = React.useMemo(
    () =>
      medias.filter(
        (m) =>
          !m.hasErrorInMedia &&
          m.mediaProcessed &&
          !!m.fileUrl &&
          getMediaKind(m.fileName || m.fileUrl, m.mediaType) === 'image',
      ),
    [medias],
  );

  const handleImageClick = React.useCallback(
    (fileUrl: string) => {
      const idx = processedImages.findIndex((m) => m.fileUrl === fileUrl);
      Lightbox.openLightbox(
        processedImages.map((m) => m.fileUrl),
        idx >= 0 ? idx : 0,
        processedImages.map((m) => m.fileName),
      );
    },
    [processedImages],
  );

  if (!medias.length && !visibleGhosts.length) return null;

  return (
    <Wrap mt="2" spacing="2">
      {/* Mídias já vêm ordenadas por position (crescente) pelo backend */}
      {medias.map((media) => {
        const kind = getMediaKind(
          media.fileName || media.fileUrl || media.content,
          media.mediaType,
        );

        // 1. Erro no processamento
        if (media.hasErrorInMedia) {
          return (
            <WrapItem key={media.id}>
              <Center
                w={THUMB_SIZE}
                h={THUMB_SIZE}
                bg="red.50"
                borderWidth="thin"
                borderColor="red.200"
                borderRadius="md"
                p="1"
              >
                <Text
                  fontSize="xs"
                  color="red.500"
                  textAlign="center"
                  noOfLines={3}
                >
                  {t('error.mediaProcess')}
                </Text>
              </Center>
            </WrapItem>
          );
        }

        // 2. Ainda processando
        if (!media.mediaProcessed || !media.fileUrl) {
          const isCancelled = cancelledMedias.has(media.id);
          const isTemp = media.id.includes('temp-media');

          // Imagem com preview disponível (Firebase URL do optimistic update)
          if (kind === 'image' && !!media.content) {
            return (
              <WrapItem key={media.id}>
                <Box position="relative" w={THUMB_SIZE} h={THUMB_SIZE}>
                  <Box
                    position="relative"
                    w={THUMB_SIZE}
                    h={THUMB_SIZE}
                    borderRadius="md"
                    overflow="hidden"
                    bg="gray.200"
                    cursor={isCancelled ? 'pointer' : 'default'}
                    onClick={
                      isCancelled ? () => dismissGhost(media.id) : undefined
                    }
                  >
                    <Image
                      src={media.content}
                      position="absolute"
                      top="0"
                      left="0"
                      w="full"
                      h="full"
                      objectFit="cover"
                      filter="blur(4px)"
                      transform="scale(1.12)"
                    />
                    <Center
                      position="absolute"
                      top="0"
                      left="0"
                      w="full"
                      h="full"
                      bg="blackAlpha.400"
                    >
                      {isCancelled ? (
                        <Circle size="10" bg="whiteAlpha.300">
                          <Icon
                            as={FiUploadCloud}
                            color="white"
                            fontSize="20px"
                          />
                        </Circle>
                      ) : (
                        <CircularProgress
                          isIndeterminate
                          size="32px"
                          color="white"
                          trackColor="whiteAlpha.400"
                          thickness="3px"
                        />
                      )}
                    </Center>
                  </Box>
                  {!isCancelled && !isTemp && (
                    <Circle
                      as="button"
                      size="5"
                      bg="blackAlpha.700"
                      position="absolute"
                      top="1"
                      right="1"
                      zIndex="1"
                      cursor="pointer"
                      onClick={() => handleCancel(media)}
                      aria-label={t('common.cancel')}
                    >
                      <Icon as={FiX} color="white" fontSize="10px" />
                    </Circle>
                  )}
                </Box>
              </WrapItem>
            );
          }

          // Imagem sem preview — skeleton simples
          if (kind === 'image') {
            return (
              <WrapItem key={media.id}>
                <Skeleton
                  w={THUMB_SIZE}
                  h={THUMB_SIZE}
                  borderRadius="md"
                  startColor="gray.100"
                  endColor="gray.300"
                />
              </WrapItem>
            );
          }

          // Documento — chip com ícone colorido em blur + spinner/upload sobre o ícone
          const loadingConfig = getFileChipConfig(kind, media.fileName);
          return (
            <WrapItem key={media.id} w="full">
              <Flex
                align="center"
                gap="3"
                p="2"
                borderWidth="thin"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
                w="full"
                cursor={isCancelled ? 'pointer' : 'default'}
                onClick={isCancelled ? () => dismissGhost(media.id) : undefined}
              >
                <Box position="relative" w="10" h="10" flexShrink={0}>
                  <Center
                    w="10"
                    h="10"
                    borderRadius="md"
                    bg={loadingConfig.bg}
                    filter="blur(3px)"
                  >
                    <Icon
                      as={loadingConfig.icon}
                      color="white"
                      fontSize="20px"
                    />
                  </Center>
                  <Center
                    position="absolute"
                    top="0"
                    left="0"
                    w="full"
                    h="full"
                  >
                    {isCancelled ? (
                      <Icon as={FiUploadCloud} color="white" fontSize="18px" />
                    ) : (
                      <CircularProgress
                        isIndeterminate
                        size="28px"
                        color="white"
                        trackColor="whiteAlpha.400"
                        thickness="3px"
                      />
                    )}
                  </Center>
                </Box>
                <Box flex="1" minW="0">
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.400"
                    wordBreak="break-word"
                  >
                    {media.fileName}
                  </Text>
                </Box>
                {!isCancelled && !isTemp && (
                  <Circle
                    as="button"
                    size="5"
                    bg="gray.300"
                    flexShrink={0}
                    cursor="pointer"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleCancel(media);
                    }}
                    aria-label={t('common.cancel')}
                  >
                    <Icon as={FiX} color="gray.600" fontSize="10px" />
                  </Circle>
                )}
              </Flex>
            </WrapItem>
          );
        }

        // 3. Processado — renderizar por tipo
        if (kind === 'image') {
          return (
            <WrapItem key={media.id}>
              <Image
                src={media.fileUrl}
                w={THUMB_SIZE}
                h={THUMB_SIZE}
                objectFit="cover"
                borderRadius="md"
                cursor="pointer"
                onClick={() => handleImageClick(media.fileUrl)}
                alt={media.fileName}
                fallback={
                  <Skeleton w={THUMB_SIZE} h={THUMB_SIZE} borderRadius="md" />
                }
              />
            </WrapItem>
          );
        }

        if (kind === 'video') {
          return (
            <WrapItem key={media.id}>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <Box
                as="video"
                src={media.fileUrl}
                controls
                maxW="220px"
                borderRadius="md"
              />
            </WrapItem>
          );
        }

        if (kind === 'audio') {
          return (
            <WrapItem key={media.id} w="full">
              <CommentAudioPlayer
                src={media.fileUrl}
                fileName={media.fileName}
              />
            </WrapItem>
          );
        }

        // pdf / other — chip de download estilo WhatsApp
        const chipConfig = getFileChipConfig(kind, media.fileName);
        const ext =
          (media.fileName || '').split('.').pop()?.toUpperCase() ?? '';
        const sizeLabel = media.fileSize
          ? formatFileSize(media.fileSize, i18n.language)
          : null;
        const metaLabel = sizeLabel ? `${ext} • ${sizeLabel}` : ext;

        return (
          <WrapItem key={media.id} w="full">
            <Flex
              align="center"
              gap="3"
              p="2"
              borderWidth="thin"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
              _hover={{ bg: 'gray.50' }}
              w="full"
              cursor="pointer"
              onClick={() =>
                openViaProxy(
                  media.fileUrl,
                  media.fileName,
                  kind === 'pdf' ? 'preview' : 'download',
                )
              }
            >
              <Center
                w="10"
                h="10"
                borderRadius="md"
                bg={chipConfig.bg}
                flexShrink={0}
              >
                <Icon as={chipConfig.icon} color="white" fontSize="20px" />
              </Center>
              <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="medium" wordBreak="break-word">
                  {media.fileName}
                </Text>
                {metaLabel && (
                  <Text fontSize="xs" color="gray.500" mt="0.5">
                    {metaLabel}
                  </Text>
                )}
              </Box>
              <Circle size="7" bg="blue.500" flexShrink={0}>
                <Icon as={FiDownload} color="white" fontSize="12px" />
              </Circle>
            </Flex>
          </WrapItem>
        );
      })}

      {/* Ghosts: mídias canceladas mantidas localmente até o autor dispensar */}
      {visibleGhosts.map((ghost) => {
        const kind = getMediaKind(
          ghost.fileName || ghost.content,
          ghost.mediaType,
        );
        const isImage = kind === 'image' && !!ghost.content;

        if (isImage) {
          return (
            <WrapItem key={`ghost-${ghost.id}`}>
              <Box position="relative" w={THUMB_SIZE} h={THUMB_SIZE}>
                <Box
                  w={THUMB_SIZE}
                  h={THUMB_SIZE}
                  borderRadius="md"
                  overflow="hidden"
                  bg="gray.200"
                >
                  <Image
                    src={ghost.content}
                    position="absolute"
                    top="0"
                    left="0"
                    w="full"
                    h="full"
                    objectFit="cover"
                    filter="blur(4px)"
                    transform="scale(1.12)"
                  />
                  <Center
                    position="absolute"
                    top="0"
                    left="0"
                    w="full"
                    h="full"
                    bg="blackAlpha.400"
                  >
                    <Circle
                      size="10"
                      bg="whiteAlpha.300"
                      cursor="pointer"
                      onClick={() => handleRetry(ghost)}
                    >
                      <Icon as={FiUploadCloud} color="white" fontSize="20px" />
                    </Circle>
                  </Center>
                </Box>
                <Circle
                  as="button"
                  size="5"
                  bg="blackAlpha.700"
                  position="absolute"
                  top="1"
                  right="1"
                  zIndex="1"
                  cursor="pointer"
                  onClick={() => dismissGhost(ghost.id)}
                  aria-label={t('common.cancel')}
                >
                  <Icon as={FiX} color="white" fontSize="10px" />
                </Circle>
              </Box>
            </WrapItem>
          );
        }

        const ghostConfig = getFileChipConfig(kind, ghost.fileName);
        return (
          <WrapItem key={`ghost-${ghost.id}`} w="full">
            <Flex
              align="center"
              gap="3"
              p="2"
              borderWidth="thin"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
              w="full"
            >
              <Box position="relative" w="10" h="10" flexShrink={0}>
                <Center
                  w="10"
                  h="10"
                  borderRadius="md"
                  bg={ghostConfig.bg}
                  filter="blur(3px)"
                >
                  <Icon as={ghostConfig.icon} color="white" fontSize="20px" />
                </Center>
                <Center
                  position="absolute"
                  top="0"
                  left="0"
                  w="full"
                  h="full"
                  cursor="pointer"
                  onClick={() => handleRetry(ghost)}
                >
                  <Icon as={FiUploadCloud} color="white" fontSize="18px" />
                </Center>
              </Box>
              <Box flex="1" minW="0">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.400"
                  wordBreak="break-word"
                >
                  {ghost.fileName}
                </Text>
              </Box>
              <Circle
                as="button"
                size="5"
                bg="gray.300"
                flexShrink={0}
                cursor="pointer"
                onClick={() => dismissGhost(ghost.id)}
                aria-label={t('common.cancel')}
              >
                <Icon as={FiX} color="gray.600" fontSize="10px" />
              </Circle>
            </Flex>
          </WrapItem>
        );
      })}
    </Wrap>
  );
};
